/**
 * @file        src/pages/platform/AddPlatformAdminUser.jsx
 * @description Create / edit an admin-console login account. The tenant
 *              picker only appears (and is required) for role='tenant_admin';
 *              a super_admin never has a tenant_id — getting this pairing
 *              wrong makes the account unrejectable by host (see
 *              config/roles.js's hostMatchesUser on the backend). Password
 *              required on create, optional on edit (blank = keep). Reused
 *              for /platform/admin-users/new and .../:id/edit.
 * @author      Kinetika Labs
 * @created     2026-07-27
 * @changelog
 *   - 2026-09-16 Claude — Password fields carry autoComplete="new-password" +
 *                          data-lpignore / data-1p-ignore, so the browser and
 *                          password managers stop pre-filling the signed-in
 *                          admin's own password into them. Only Login.jsx keeps
 *                          "current-password".
 *   - 2026-07-27 Claude — Initial (multi-tenant super admin platform).
 *   - 2026-09-17 Claude — Password + confirm are now <PasswordInput>
 *                          (reveal toggle).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import PasswordInput from '../../components/PasswordInput';

const ROLE_OPTIONS = [
  { value: 'tenant_admin', label: 'Tenant Admin' },
  { value: 'super_admin', label: 'Super Admin' },
];
const STATUS = [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }];

const empty = {
  role: 'tenant_admin', tenant_id: '', email: '', first_name: '', last_name: '',
  password: '', confirm: '', is_active: true,
};

export default function AddPlatformAdminUser() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const { data: tenantsData } = useQuery({
    queryKey: qk.tenants.list({ status: 'active', pageSize: 200 }),
    queryFn: () => get('/tenants', { params: { status: 'active', pageSize: 200 } }),
  });
  const tenantOptions = (tenantsData?.data || []).map((t) => ({ value: String(t.id), label: t.name }));

  const { data: existing } = useQuery({
    queryKey: qk.platformAdminUsers.one(id),
    queryFn: () => get(`/platform-admin-users/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing?.data) {
      const u = existing.data;
      setForm({
        role: u.role, tenant_id: u.tenant_id ? String(u.tenant_id) : '',
        email: u.email || '', first_name: u.first_name || '', last_name: u.last_name || '',
        password: '', confirm: '', is_active: !!u.is_active,
      });
    }
  }, [existing]);

  const mut = useMutation({
    mutationFn: (body) => (isEdit ? patch(`/platform-admin-users/${id}`, body) : post('/platform-admin-users', body)),
    onSuccess: () => {
      toast.ok(isEdit ? 'Admin user updated' : 'Admin user created', `${form.email} saved.`);
      qc.invalidateQueries({ queryKey: ['platform-admin-users'] });
      navigate('/platform/admin-users');
    },
    onError: (err) => {
      const fields = err.response?.data?.error?.fields;
      if (fields) setErrors(fields);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function validate() {
    const fe = {};
    if (!form.email.trim()) fe.email = 'Required';
    if (form.role === 'tenant_admin' && !form.tenant_id) fe.tenant_id = 'Required for Tenant Admin';
    if (!isEdit && !form.password) fe.password = 'Required';
    if (form.password && form.password.length < 10) fe.password = 'Min 10 characters';
    if (form.password && form.password !== form.confirm) fe.confirm = 'Passwords do not match';
    return fe;
  }

  function onSubmit(e) {
    e.preventDefault();
    const fe = validate();
    if (Object.keys(fe).length) return setErrors(fe);
    setErrors({});

    const body = {
      role: form.role,
      email: form.email.trim().toLowerCase(),
      first_name: form.first_name || undefined,
      last_name: form.last_name || undefined,
      is_active: form.is_active,
      tenant_id: form.role === 'tenant_admin' ? Number(form.tenant_id) : null,
    };
    if (form.password) body.password = form.password;
    mut.mutate(body);
  }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit Admin User' : 'Add Admin User'}
        subtitle="Platform · Platform Admin Users"
        actions={<button className="btn ghost" onClick={() => navigate('/platform/admin-users')}><Icon name="x" /> Cancel</button>}
      />

      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          <Field label="Role" required>
            <Select
              value={form.role}
              onChange={(v) => setForm({ ...form, role: v, tenant_id: v === 'super_admin' ? '' : form.tenant_id })}
              options={ROLE_OPTIONS}
            />
          </Field>
          {form.role === 'tenant_admin' && (
            <Field label="Tenant" required err={errors.tenant_id}>
              <Select value={form.tenant_id} onChange={(v) => setForm({ ...form, tenant_id: v })}
                      options={tenantOptions} placeholder="Select tenant" />
            </Field>
          )}
          <Field label="Status">
            <Select value={form.is_active ? 'active' : 'inactive'}
                    onChange={(v) => setForm({ ...form, is_active: v === 'active' })} options={STATUS} />
          </Field>

          <Field label="First Name">
            <input value={form.first_name} onChange={on('first_name')} placeholder="First name" />
          </Field>
          <Field label="Last Name">
            <input value={form.last_name} onChange={on('last_name')} placeholder="Last name" />
          </Field>
          <Field label="Email" required err={errors.email}>
            <input value={form.email} onChange={on('email')} placeholder="Email ID" />
          </Field>

          <Field label="Password" required={!isEdit} err={errors.password}
                 hint={isEdit ? 'Leave blank to keep current password.' : 'Min 10 characters.'}>
            <PasswordInput value={form.password} onChange={on('password')} placeholder="Login password" autoComplete="new-password" data-lpignore="true" data-1p-ignore />
          </Field>
          <Field label="Confirm Password" required={!isEdit || !!form.password} err={errors.confirm}>
            <PasswordInput value={form.confirm} onChange={on('confirm')} placeholder="Confirm password" autoComplete="new-password" data-lpignore="true" data-1p-ignore />
          </Field>

          <div className="form-actions">
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Admin User'}
            </button>
          </div>
        </form>
      </article>
    </>
  );
}

function Field({ label, required, err, hint, children }) {
  return (
    <div className="field">
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {hint && !err ? <div className="field-hint muted">{hint}</div> : null}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
